/**
 * Einen Auszug einem Mandanten zuordnen. Als Fenster in der Bildschirmmitte,
 * mit Suche statt Klappliste, und mit der Moeglichkeit, den Mandanten gleich
 * hier anzulegen, wenn es ihn noch nicht gibt.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type MandantZeile } from '../lib/api'
import { iban as ibanLesbar } from '../lib/format'
import { Fenster } from './Fenster'
import { Filterfeld } from './Filterfeld'

interface Eigenschaften {
  offen: boolean
  aufSchliessen: () => void
  auszugId: string
  iban: string | null
  inhaber: string | null
  /** Bei der Kartenabrechnung: die Kontokennung und wie die Karte angezeigt wird. */
  karte?: { kennung: string | null; text: string } | undefined
}

export function MandantWaehlen({ offen, aufSchliessen, auszugId, iban, inhaber, karte }: Eigenschaften) {
  const abfragen = useQueryClient()
  const [suche, setzeSuche] = useState('')
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null)
  const [merken, setzeMerken] = useState(true)
  // Der Kontoinhaber vom Auszug ist der naheliegende Name fuer einen neuen Mandanten.
  const [neuerName, setzeNeuerName] = useState(inhaber ?? '')
  const [fehler, setzeFehler] = useState<string | null>(null)
  // Ist ein neuer Mandant schon angelegt und scheitert danach das Zuordnen,
  // wird beim zweiten Versuch kein zweiter mit demselben Namen angelegt.
  const angelegt = useRef<{ name: string; id: string } | null>(null)

  const liste = useQuery({
    queryKey: ['mandanten', suche],
    queryFn: () => api.holen<{ mandanten: MandantZeile[] }>(`/api/mandanten${suche ? `?suche=${encodeURIComponent(suche)}` : ''}`),
    enabled: offen,
  })

  /* Gelernter Vorschlag: Der Inhaber hat schon ein Konto oder eine Karte bei
     genau einem Mandanten. Vorgewaehlt, aber erst der Klick ordnet zu. */
  const vorschlag = useQuery({
    queryKey: ['vorschlag', auszugId],
    queryFn: () => api.holen<{ mandant: { id: string; name: string } | null; grund?: string }>(`/api/auszuege/${auszugId}/vorschlag`),
    enabled: offen,
  })
  useEffect(() => {
    const m = vorschlag.data?.mandant
    if (!m || gewaehlt) return
    setzeGewaehlt(m.id)
    setzeNeuerName('')
  }, [vorschlag.data, gewaehlt])

  /* Gibt es schon einen Mandanten, der genau so heisst wie der Kontoinhaber,
     ist er vorgewaehlt. Nur bei exakt gleichem Namen: aehnlich ist ein
     Vorschlag, den ein Mensch bestaetigen soll. */
  useEffect(() => {
    if (!inhaber || gewaehlt || !liste.data) return
    const gleich = liste.data.mandanten.find((m) => m.name.trim().toLowerCase() === inhaber.trim().toLowerCase())
    if (gleich) {
      setzeGewaehlt(gleich.id)
      setzeNeuerName('')
    }
  }, [liste.data, inhaber, gewaehlt])

  const zuordnen = useMutation({
    mutationFn: async () => {
      let mandantId = gewaehlt
      if (!mandantId && neuerName.trim()) {
        const name = neuerName.trim()
        if (angelegt.current?.name !== name) {
          angelegt.current = { name, id: (await api.senden<{ id: string }>('/api/mandanten', { name })).id }
        }
        mandantId = angelegt.current.id
      }
      if (!mandantId) throw new ApiFehler(400, 'Bitte einen Mandanten wählen oder einen Namen für einen neuen eingeben.')
      await api.senden(`/api/auszuege/${auszugId}/zuordnen`, { mandantId, ibanMerken: merken })
    },
    onSuccess: async () => {
      await abfragen.invalidateQueries()
      schliessen()
    },
    onError: (problem) => setzeFehler(problem instanceof ApiFehler ? problem.message : 'Die Zuordnung hat nicht geklappt.'),
  })

  function schliessen() {
    setzeSuche('')
    setzeGewaehlt(null)
    setzeNeuerName(inhaber ?? '')
    setzeFehler(null)
    setzeMerken(true)
    angelegt.current = null
    aufSchliessen()
  }

  function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    zuordnen.mutate()
  }

  return (
    <Fenster titel="Mandant zuordnen" offen={offen} aufSchliessen={schliessen}>
      <form className="formular" onSubmit={absenden}>
        {fehler && <div className="fehler">{fehler}</div>}

        <div className="leiser">
          {karte ? 'Karte' : 'Konto'} <span className="zahl">{karte ? karte.text : ibanLesbar(iban)}</span>
          {inhaber ? `, Inhaber laut ${karte ? 'Abrechnung' : 'Auszug'}: ${inhaber}` : ''}
        </div>
        {vorschlag.data?.mandant && (
          <div className="hinweis">Vorschlag: <strong>{vorschlag.data.mandant.name}</strong>. {vorschlag.data.grund}</div>
        )}

        {/* Enter im Suchfeld darf das Formular nicht abschicken: das hiesse
            "neuen Mandanten anlegen", und zwar mit dem Kontoinhaber als Namen. */}
        <div onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}>
          <Filterfeld className="suchfeld-voll" wert={suche} setzeWert={setzeSuche} beschriftung="Mandanten suchen" platzhalter="Name, Nummer oder IBAN" />
        </div>

        <div className="wahlliste" role="listbox" aria-label="Mandanten">
          {liste.isPending && <div className="leiser">Wird geladen …</div>}
          {liste.data?.mandanten.length === 0 && <div className="leiser">Kein Mandant gefunden.</div>}
          {liste.data?.mandanten.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={gewaehlt === m.id}
              className="wahlzeile"
              onClick={() => {
                setzeGewaehlt(m.id)
                setzeNeuerName('')
              }}
            >
              <span>{m.name}</span>
              {m.mandantennummer !== null && <span className="leiser zahl">{m.mandantennummer}</span>}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor="mw-neu">Oder neuen Mandanten anlegen</label>
          <input
            id="mw-neu"
            type="text"
            autoComplete="off"
            data-1p-ignore
            placeholder={inhaber ?? 'Name des Mandanten'}
            value={neuerName}
            onChange={(e) => {
              setzeNeuerName(e.target.value)
              if (e.target.value) setzeGewaehlt(null)
            }}
          />
        </div>

        {(karte ? karte.kennung : iban) && (
          <label className="schalter">
            <input type="checkbox" checked={merken} onChange={(e) => setzeMerken(e.target.checked)} />
            {karte
              ? 'Karte beim Mandanten merken. Künftige Abrechnungen dieser Karte ordnen sich dann selbst zu.'
              : 'IBAN beim Mandanten merken. Künftige Auszüge dieses Kontos ordnen sich dann selbst zu.'}
          </label>
        )}

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={schliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={zuordnen.isPending || (!gewaehlt && !neuerName.trim())}>
            {zuordnen.isPending ? 'Wird zugeordnet …' : 'Zuordnen'}
          </button>
        </div>
      </form>
    </Fenster>
  )
}
